'use client'

import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Typography from '@mui/material/Typography'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined'
import LocalPharmacyOutlinedIcon from '@mui/icons-material/LocalPharmacyOutlined'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import type { MessageDescriptor } from '@lingui/core'
import { LOCATION_OPTIONS, LocationOption } from '../lib/patient-location.types'

const OPTION_ICONS: Record<LocationOption, React.ElementType> = {
  HOME: HomeOutlinedIcon,
  TLC_CABIN: MedicalServicesOutlinedIcon,
  PHARMACY: LocalPharmacyOutlinedIcon,
  FRANCE_SERVICES: AccountBalanceOutlinedIcon,
  HEALTH_CENTER: HealthAndSafetyOutlinedIcon,
  OTHER: MoreHorizIcon,
}

export const OPTION_LABELS: Record<LocationOption, MessageDescriptor> = {
  HOME: msg`Home`,
  TLC_CABIN: msg`Teleconsultation cabin`,
  PHARMACY: msg`Pharmacy`,
  FRANCE_SERVICES: msg`France Services house`,
  HEALTH_CENTER: msg`Health center`,
  OTHER: msg`Other location`,
}

type LocationQuickPickProps = {
  selected: LocationOption | null
  onSelect: (option: LocationOption) => void
}

export function LocationQuickPick({ selected, onSelect }: LocationQuickPickProps) {
  const { i18n } = useLingui()
  const gridOptions = LOCATION_OPTIONS.filter((o) => o !== 'OTHER')

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        {gridOptions.map((option) => {
          const Icon = OPTION_ICONS[option]
          const isActive = selected === option
          return (
            <ButtonBase
              key={option}
              onClick={() => onSelect(option)}
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                p: 2.5,
                borderRadius: 3,
                textAlign: 'left',
                transition: 'all 0.2s',
                bgcolor: isActive ? 'primary.light' : 'grey.50',
                border: 2,
                borderColor: isActive ? 'primary.main' : 'transparent',
                '&:hover': {
                  bgcolor: isActive ? 'primary.light' : 'grey.100',
                  borderColor: isActive ? 'primary.main' : 'grey.200',
                },
              }}
            >
              <Box sx={{ mb: 1.5, color: isActive ? 'primary.main' : 'text.secondary' }}>
                <Icon sx={{ fontSize: 28 }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: isActive ? 700 : 600, lineHeight: 1.3, color: isActive ? 'primary.dark' : 'text.primary' }}>
                {i18n._(OPTION_LABELS[option])}
              </Typography>
              {isActive && (
                <CheckCircleIcon
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    fontSize: 20,
                    color: 'primary.main',
                  }}
                />
              )}
            </ButtonBase>
          )
        })}
      </Box>

      <ButtonBase
        onClick={() => onSelect('OTHER')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 1.5,
          borderRadius: 2,
          bgcolor: selected === 'OTHER' ? 'primary.light' : 'grey.50',
          border: 2,
          borderColor: selected === 'OTHER' ? 'primary.main' : 'transparent',
          transition: 'all 0.2s',
          '&:hover': {
            bgcolor: selected === 'OTHER' ? 'primary.light' : 'grey.100',
          },
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: selected === 'OTHER' ? 'primary.main' : 'text.secondary',
          }}
        >
          <MoreHorizIcon />
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, textAlign: 'left' }}>
          {i18n._(OPTION_LABELS.OTHER)}
        </Typography>
        <ChevronRightIcon sx={{ color: 'text.disabled' }} />
      </ButtonBase>
    </Box>
  )
}
